import { useState } from "react";
import { motion } from "framer-motion";
import { loginUser } from "../api/auth";
import "./auth.css";

const getErrorMessage = (err: any): string => {
  if (!err) return "Something went wrong. Please try again.";

  if (typeof err === "string") return err;

  if (Array.isArray(err)) {
    return err.map((e) => e.msg || "Invalid input").join(", ");
  }

  if (typeof err === "object" && err.msg) {
    return err.msg;
  }

  return "Something went wrong. Please try again.";
};

interface Props {
  onSuccess(): void;
}

export default function Login({ onSuccess }: Props) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null);

    if (!email || !password) {
      setError("Enter email and password.");
      return;
    }

    try {
      setLoading(true);
      const res = await loginUser({ email, password });
      localStorage.setItem("token", res.data.access_token);
      onSuccess();
    } catch (e: any) {
      const detail = e.response?.data?.detail;
      setError(getErrorMessage(detail));
    }
    finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      className="auth-page animated-bg"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        className="register-shell"
        initial={{ scale: 0.95, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
      >
        {/* LEFT */}
        <div className="register-left">
          <h1>Welcome back</h1>
          <p>
            Sign in to continue your work and manage your AI-powered
            content experience.
          </p>
        </div>

        {/* RIGHT */}
        <div className="register-right">
          <div className="field">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <label>Email address</label>
          </div>

          <div className="field">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <label>Password</label>
          </div>

          {error && <div className="auth-error">{error}</div>}

          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={submit}
            disabled={loading}
          >
            {loading ? "Signing in…" : "Sign in"}
          </motion.button>

          <div className="auth-footer">
            Don’t have an account?
            <span onClick={() => (window.location.href = "/register")}>
              Create one
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
